import { useCallback, useEffect, useState } from "react";

import type { BlsState } from "../domain/blsChannel";
import type { BlsEdge } from "../domain/blsMotion";
import { TACTILE_PULSE_MS, tactilePulse } from "../domain/blsTactile";
import { useBlsEdges } from "./useBlsEdges";

type Options = {
    state: BlsState | null;
    cyclesNow: (nowEpochMs?: number) => number;
    /** Off on the therapist's screen: their own controller is not the patient's. */
    enabled?: boolean;
};

export type BlsHaptics = {
    /** Whether this browser can drive controller vibration at all. */
    supported: boolean;
    /** Controllers connected that can vibrate, at most two used. */
    controllers: number;
};

function gamepadsApi(): (() => (Gamepad | null)[]) | null {
    if (typeof navigator === "undefined" || typeof navigator.getGamepads !== "function") return null;
    return () => Array.from(navigator.getGamepads());
}

function canRumble(gamepad: Gamepad | null): gamepad is Gamepad {
    const actuator = gamepad?.vibrationActuator;
    if (!gamepad?.connected || !actuator || typeof actuator.playEffect !== "function") return false;
    // Older Chromium has no `effects` list but does dual-rumble; trust it there.
    const effects = (actuator as { effects?: readonly string[] }).effects;
    return !effects || effects.includes("dual-rumble");
}

function rumblePads(): Gamepad[] {
    const read = gamepadsApi();
    return read ? read().filter(canRumble).slice(0, 2) : [];
}

/**
 * Tactile bilateral stimulation through game controllers.
 *
 * The Gamepad API is the one way a web page can make something in the
 * patient's hands vibrate without installing anything: Chromium browsers
 * drive dual-rumble on Xbox, PlayStation and Switch controllers. Phones'
 * own vibration was left out — iOS does not expose it, and one phone cannot
 * tell left from right.
 *
 * Browsers only reveal a controller after a button on it is pressed, which is
 * why the count listens for `gamepadconnected` rather than reading once.
 */
export function useBlsHaptics({ state, cyclesNow, enabled = true }: Options): BlsHaptics {
    const supported = gamepadsApi() !== null;
    const [controllers, setControllers] = useState(() => rumblePads().length);

    useEffect(() => {
        if (!supported) return;
        const refresh = () => setControllers(rumblePads().length);
        window.addEventListener("gamepadconnected", refresh);
        window.addEventListener("gamepaddisconnected", refresh);
        return () => {
            window.removeEventListener("gamepadconnected", refresh);
            window.removeEventListener("gamepaddisconnected", refresh);
        };
    }, [supported]);

    const onEdge = useCallback((edge: BlsEdge) => {
        // Read fresh each time: a Gamepad object is a snapshot in some browsers.
        const pads = rumblePads();
        const target = tactilePulse(edge, pads.length);
        if (!target) return;
        void pads[target.slot]?.vibrationActuator
            ?.playEffect("dual-rumble", {
                duration: TACTILE_PULSE_MS,
                strongMagnitude: target.strongMagnitude,
                weakMagnitude: target.weakMagnitude,
            })
            .catch(() => undefined);
    }, []);

    const active = enabled && supported && state !== null && state.settings.tactile;
    useBlsEdges({ state, cyclesNow, active, onEdge });

    return { supported, controllers: supported ? controllers : 0 };
}
